// src/pages/TripPlanner/AIAssistant.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, Paper, Avatar, CircularProgress, Divider } from '@mui/material';
import { useTripPlanner } from './TripPlannerContext';
import SendIcon from '@mui/icons-material/Send';
import RobotIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}

const AIAssistant: React.FC = () => {
  const { tripPlan } = useTripPlanner();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initiate conversation with a welcome message
  useEffect(() => {
    const initialMessage: Message = {
      id: 'welcome',
      text: "Hi! I'm your AI trip planning assistant. I can help you discover destinations, find activities, or answer questions about your trip. What would you like to know?",
      sender: 'assistant',
      timestamp: new Date()
    };
    setMessages([initialMessage]);
  }, []);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Generate context about the current trip for the AI
  const generateContext = () => {
    let context = "";
    
    if (tripPlan.destination) {
      context += `Trip to ${tripPlan.destination.name}, ${tripPlan.destination.country}. `;
    }
    
    if (tripPlan.startDate && tripPlan.endDate) {
      context += `From ${tripPlan.startDate.toLocaleDateString()} to ${tripPlan.endDate.toLocaleDateString()}. `;
    }
    
    if (tripPlan.accommodations.length > 0) {
      context += `Staying at ${tripPlan.accommodations.map(a => a.name).join(', ')}. `;
    }
    
    if (tripPlan.activities.length > 0) {
      context += `Planned activities: ${tripPlan.activities.map(a => a.title).join(', ')}. `;
    }
    
    return context;
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    
    // Add user message to chat
    const userMessage: Message = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      // Prepare context for the AI
      const context = generateContext();
      
      // Call Claude API
      const response = await fetch('https://api.perplexity.ai/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer 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`
        },
        body: JSON.stringify({
          model: 'claude-3-opus-20240229',
          messages: [
            {
              role: 'system',
              content: `You are a helpful travel planning assistant.
              The user is planning a trip.
              Current trip context: ${context}
              Provide relevant, specific, and actionable advice.
              If the user asks about activities, recommend specific places.
              If they ask about accommodations, suggest specific options.
              Keep recommendations practical and related to their destination if available.
              If no destination has been selected yet, help them choose one based on their preferences.`
            },
            ...messages
              .filter(m => m.id !== 'welcome') // Skip welcome message
              .map(m => ({
                role: m.sender === 'user' ? 'user' : 'assistant',
                content: m.text
              })),
            {
              role: 'user',
              content: input
            }
          ],
          max_tokens: 1000
        })
      });
      
      const data = await response.json();
      
      // Add assistant response to chat
      const assistantMessage: Message = {
        id: Date.now().toString(),
        text: data.choices[0]?.message?.content || "I'm sorry, I couldn't generate a response. Please try again.",
        sender: 'assistant',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error calling AI API:', error);
      
      // Add error message
      const errorMessage: Message = {
        id: Date.now().toString(),
        text: "I'm sorry, I encountered an error. Please try again later.",
        sender: 'assistant',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      <Typography variant="h6" gutterBottom>
        AI Trip Assistant
      </Typography>
      
      <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
        {messages.map((message) => (
          <Box 
            key={message.id}
            sx={{ 
              display: 'flex', 
              justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
              mb: 2 
            }}
          >
            {message.sender === 'assistant' && (
              <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                <RobotIcon />
              </Avatar>
            )}
            
            <Paper 
              elevation={1}
              sx={{ 
                p: 2, 
                maxWidth: '70%',
                bgcolor: message.sender === 'user' ? 'primary.light' : 'background.paper',
                color: message.sender === 'user' ? 'white' : 'text.primary',
                borderRadius: 2
              }}
            >
              <Typography variant="body1">
                {message.text}
              </Typography>
              <Typography variant="caption" color={message.sender === 'user' ? 'white' : 'text.secondary'}>
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Paper>
            
            {message.sender === 'user' && (
              <Avatar sx={{ bgcolor: 'secondary.main', ml: 1 }}>
                <PersonIcon />
              </Avatar>
            )}
          </Box>
        ))}
        
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
              <RobotIcon />
            </Avatar>
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography>Thinking...</Typography>
            </Paper>
          </Box>
        )}
        
        <div ref={messagesEndRef} />
      </Box>
      
      <Divider sx={{ mb: 2 }} />
      
      <Box sx={{ display: 'flex' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Ask anything about your trip planning..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSendMessage();
            }
          }}
          disabled={isLoading}
        />
        <Button 
          variant="contained" 
          endIcon={<SendIcon />}
          onClick={handleSendMessage}
          disabled={isLoading || !input.trim()}
          sx={{ ml: 1 }}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
};

export default AIAssistant;